import React, { useState, useEffect } from 'react';
import { User, Activity, SegmentEffort } from '../../types';
import Chat from '../messenger/Chat';
import { API_BASE_URL } from '../../config';
import './TraineeDetail.css';

interface TraineeDetailProps {
    trainee: User;
    currentUser: User;
    onBack: () => void;
}

const TraineeDetail: React.FC<TraineeDetailProps> = ({ trainee, currentUser, onBack }) => {
    const [recommendations, setRecommendations] = useState(trainee.recommendations || '');
    const [activities, setActivities] = useState<Activity[]>([]);
    const [segmentEfforts, setSegmentEfforts] = useState<SegmentEffort[]>([]);
    const [selectedActivityName, setSelectedActivityName] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    const fetchActivities = async (page: number) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/trainees/${trainee.id}/activities?page=${page}&per_page=5`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setActivities(data.activities);
                setTotalPages(data.total_pages);
                setCurrentPage(data.current_page);
                // Clear segment efforts when changing activity page
                setSegmentEfforts([]);
                setSelectedActivityName(null);
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
        }
    };

    const fetchSegmentEfforts = async (activityId: number, activityName: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/activities/${activityId}/segment_efforts`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setSegmentEfforts(data); // The endpoint now returns an array directly
                setSelectedActivityName(activityName);
            }
        } catch (error) {
            console.error('Error fetching segment efforts:', error);
        }
    };

    useEffect(() => {
        const markAsRead = async () => {
            try {
                await fetch(`${API_BASE_URL}/api/chat/messages/${trainee.id}/read`, {
                    method: 'POST',
                    credentials: 'include',
                });
            } catch (error) {
                console.error('Error marking messages as read:', error);
            }
        };
        markAsRead();
        fetchActivities(currentPage);
    }, [trainee.id, currentPage]);

    const handleSaveRecommendations = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/user/${trainee.id}/recommendations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ recommendations }),
            });
            if (response.ok) {
                alert('Recommendations saved!');
            } else {
                alert('Failed to save recommendations.');
            }
        } catch (error) {
            console.error('Error saving recommendations:', error);
        }
    };

    const formatDuration = (seconds: number) => {
        const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
        const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    return (
        <div>
            <button onClick={onBack}>&larr; Back to Trainees</button>
            <h1>{trainee.first_name} {trainee.last_name}</h1>
            <div className="trainee-detail-container">
                <div className="recommendations-panel">
                    <h2>Recommendations</h2>
                    <textarea
                        value={recommendations}
                        onChange={(e) => setRecommendations(e.target.value)}
                        rows={15}
                    />
                    <button onClick={handleSaveRecommendations}>Save Recommendations</button>
                </div>
                <div className="chat-panel">
                    <Chat contact={trainee} currentUser={currentUser} />
                </div>
            </div>
            <div className="card activity-panel">
                <h2>Trainee Activities</h2>
                <table className="user-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Date</th>
                            <th>Distance</th>
                            <th>Time</th>
                            <th>Elevation</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activities.map((activity) => (
                            <tr key={activity.id}>
                                <td>{activity.name}</td>
                                <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                                <td>{(activity.distance / 1000).toFixed(2)} km</td>
                                <td>{formatDuration(activity.moving_time)}</td>
                                <td>{activity.total_elevation_gain.toFixed(0)} m</td>
                                <td>
                                    {(activity.type === 'Ride' || activity.type === 'VirtualRide') && (
                                        <button onClick={() => fetchSegmentEfforts(activity.id, activity.name)}>
                                            Show Segments
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="pagination-controls">
                    <button onClick={() => fetchActivities(currentPage - 1)} disabled={currentPage <= 1}>
                        &larr; Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => fetchActivities(currentPage + 1)} disabled={currentPage >= totalPages}>
                        Next &rarr;
                    </button>
                </div>

                {selectedActivityName && (
                    <div className="segment-efforts-panel">
                        <h3>Segment Efforts for: {selectedActivityName}</h3>
                        {segmentEfforts.length > 0 ? (
                            <table className="user-table segments-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Distance</th>
                                        <th>Time</th>
                                        <th>Avg Watts</th>
                                        <th>Avg HR</th>
                                        <th>Max HR</th>
                                        <th>Avg Cadence</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {segmentEfforts.map((effort) => (
                                        <tr key={effort.id}>
                                            <td>{effort.name}</td>
                                            <td>{(effort.distance / 1000).toFixed(2)} km</td>
                                            <td>{formatDuration(effort.elapsed_time)}</td>
                                            <td>{effort.average_watts?.toFixed(0) ?? 'N/A'}</td>
                                            <td>{effort.average_heartrate?.toFixed(0) ?? 'N/A'}</td>
                                            <td>{effort.max_heartrate ?? 'N/A'}</td>
                                            <td>{effort.average_cadence?.toFixed(0) ?? 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p>No segment efforts found for this activity.</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TraineeDetail; 